// src/components/AddAds.tsx
import React, { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import Gallery from "../components/Gallery";
import "../CSS/App.css";
import { DocumentData } from "firebase/firestore";
import { Cloudinary } from "@cloudinary/url-gen";
import { uploadToCloudinary } from "../utils/cloudinary";
import axios from "axios";

interface Ad extends DocumentData {
  id: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  youtubeUrl?: string;
  createdAt: number;
  location: "Lebanon" | "Abidjan";
  type: "ad";
  showInFlipcard: boolean;
}

const AddAds: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [location, setLocation] = useState<"Lebanon" | "Abidjan">("Lebanon");
  const [showInFlipcard, setShowInFlipcard] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAdId, setSelectedAdId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const galleryInputRef = React.useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleGalleryUploadClick = () => {
    galleryInputRef.current?.click();
  };

  useEffect(() => {
    fetchAds();
  }, []);

  useEffect(() => {
    if (id) {
      fetchAdById(id);
    }
  }, [id]);

  const fetchAds = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/ad-spaces`
      );
      setAds(response.data);
    } catch (error) {
      console.error("Error fetching ads:", error);
    }
  };

  const fetchAdById = async (adId: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/ad-spaces/${id}`
      );
      const adData = response.data;
      setTitle(adData.title);
      setText(adData.text);
      setImageUrl(adData.imageUrl || "");
      setGalleryImages(adData.galleryImages || []);
      setYoutubeUrl(adData.youtubeUrl || "");
      setLocation(adData.location);
      setShowInFlipcard(adData.showInFlipcard || false);
      setSelectedAdId(adId);
      setEditMode(true);
    } catch (error) {
      console.error("Error fetching ad:", error);
    }
  };

  const validateImage = (file: File): boolean => {
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    const ALLOWED_TYPES = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(
        "Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image."
      );
      return false;
    }

    if (file.size > MAX_SIZE) {
      setError("File size too large. Please upload an image smaller than 5MB.");
      return false;
    }

    return true;
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setError("");
    const file = event.target.files?.[0];
    if (!file) return;

    if (!validateImage(file)) return;

    try {
      setUploading(true);
      const uploadedUrl = await uploadToCloudinary(file);
      setImageUrl(uploadedUrl);
    } catch (error) {
      console.error("Error uploading image:", error);
      setError("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setError("");
    const files = event.target.files;
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (validateImage(files[i])) {
        validFiles.push(files[i]);
      }
    }

    setGalleryFiles(validFiles);

    try {
      setUploading(true);
      const uploadPromises = validFiles.map(file => uploadToCloudinary(file));
      const uploadedUrls = await Promise.all(uploadPromises);
      setGalleryImages(prevImages => [...prevImages, ...uploadedUrls]);
    } catch (error) {
      console.error("Error uploading gallery images:", error);
      setError("Failed to upload gallery images. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!title || !text) {
        throw new Error("Please fill in all required fields");
      }

      const adData = {
        title,
        text,
        imageUrl,
        galleryImages,
        youtubeUrl,
        location,
        showInFlipcard,
      };

      if (editMode && selectedAdId) {
        await axios.put(
          `${process.env.REACT_APP_API}/ad-spaces/${selectedAdId}`,
          {
            ...adData,
            updatedAt: Date.now(),
          }
        );

        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Ad updated successfully",
          life: 3000,
        });
      } else {
        //check if the limit of 5 flipcards is reached
        // Make a request to check how many ads are currently set to show in Flipcard
        const response = await axios.get(
          `${process.env.REACT_APP_API}/ad-spaces?showInFlipcard=true`
        );

        if (showInFlipcard && response.data.length >= 5) {
          console.log(response.data);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              "Maximum limit of 5 flipcards reached. Please uncheck some posts first.",
            life: 5000,
          });
          setShowInFlipcard(false);
          return;
        }

        await axios.post(`${process.env.REACT_APP_API}/ad-spaces`, {
          ...adData,
          type: "ad",
          createdAt: Date.now(),
        });

        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Ad added successfully",
          life: 3000,
        });

        fetchAds();
      }

      if (location === "Lebanon") {
        navigate("/lebanon-ads");
      } else {
        navigate("/abidjan-ads");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err instanceof Error ? err.message : "An error occurred",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-ad-container">
      <Card
        title={editMode ? "Edit Ad" : "Create New Ad"}
        className="add-ad-card"
      >
        <Toast ref={toast} />
        <form onSubmit={handleSubmit} className="p-fluid">
          <div className="p-field mb-3">
            <label htmlFor="title">Title</label>
            <InputText
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="p-field mb-3">
            <label htmlFor="text">Content</label>
            <InputTextarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              required
              className="whitespace-pre-wrap w-full"
            />
          </div>

          <div className="p-field mb-3">
            <label>Location</label>
            <div className="p-d-flex p-ai-center">
              <RadioButton
                inputId="Lebanon"
                name="Location"
                value="Lebanon"
                onChange={(e) => setLocation(e.value)}
                checked={location === "Lebanon"}
              />
              <label htmlFor="Lebanon" className="p-ml-2">
                Lebanon
              </label>

              <RadioButton
                inputId="Abidjan"
                name="location"
                value="Abidjan"
                onChange={(e) => setLocation(e.value)}
                checked={location === "Abidjan"}
                className="p-ml-4"
              />
              <label htmlFor="Abidjan" className="p-ml-2">
                Abidjan
              </label>
            </div>
          </div>

          <div className="p-field mb-3">
            <label htmlFor="image">Main Image</label>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImageUpload}
              accept="image/*"
            />
            <Button
              type="button"
              label="Upload Image"
              icon="pi pi-upload"
              onClick={handleUploadClick}
              className="p-button-secondary"
            />
            {imageUrl && (
              <div className="preview-image mt-2">
                <img
                  src={imageUrl}
                  alt="Preview"
                  style={{ maxWidth: "200px" }}
                />
              </div>
            )}
          </div>

          <div className="p-field mb-3">
            <label htmlFor="gallery">Gallery Images</label>
            <input
              type="file"
              ref={galleryInputRef}
              style={{ display: "none" }}
              onChange={handleGalleryUpload}
              accept="image/*"
              multiple
            />
            <Button
              type="button"
              label="Upload Gallery Images"
              icon="pi pi-images"
              onClick={handleGalleryUploadClick}
              className="p-button-secondary"
            />
            {galleryFiles.length > 0 && (
              <Gallery
                images={galleryFiles.map((file) => URL.createObjectURL(file))}
                isEditing={true}
                onImageRemove={(index) => {
                  const newGalleryFiles = [...galleryFiles];
                  newGalleryFiles.splice(index, 1);
                  setGalleryFiles(newGalleryFiles);
                }}
              />
            )}
          </div>

          <div className="p-field mb-3">
            <label htmlFor="youtube">YouTube URL (optional)</label>
            <InputText
              id="youtube"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>

          <div className="p-field-checkbox" style={{ marginTop: "1rem" }}>
            <Checkbox
              inputId="showInFlipcard"
              checked={showInFlipcard}
              onChange={(e) => setShowInFlipcard(e.target.checked ?? false)}
            />
            <label
              htmlFor="showInFlipcard"
              className="p-checkbox-label"
              style={{ marginLeft: "0.5rem" }}
            >
              Show in Home Flipcard Section
            </label>
          </div>

          {error && <div className="p-error mb-3">{error}</div>}

          <Button
            type="submit"
            label={editMode ? "Update Ad" : "Create Ad"}
            icon="pi pi-check"
            loading={loading}
          />
        </form>
      </Card>
    </div>
  );
};

export default AddAds;
